import { getAuthTokenCookie } from '../utils/authToken';

// API Service Layer
// Types for API responses
export interface Course {
  id: string;
  title: string;
  slug: string;
  description: string;
  isPremium: boolean;
  locked: boolean;
  tags: string[];
  content?: {
    pages?: Array<{
      id: string;
      title: string;
      content: string;
      order: number;
      videoUrl?: string;    // Optional page-level YouTube video ID
    }>;
  };
  labs?: Lab[];
}

export interface Lab {
  id: string;
  title: string;
  slug: string;
  description: string;
  isPremium: boolean;
  locked: boolean;
  difficulty: string;
  level: string;
  estimatedTime: number;
  durationMins: number;
  steps?: Array<{
    id: string;
    type: string;
    title: string;
    content?: string;
    question?: string;
    choices?: string[];
    answerIndex?: number;
    explanation?: string;
    prompt?: string;
    placeholder?: string;
    solution?: string;
    // DISABLED: task step-type
    // checklist?: string[];
  }>;
}

// LabStep type for individual lab steps
type LabStep = {
  id: string;
  type: string;
  title: string;
  content?: string;
  question?: string;
  choices?: string[];
  answerIndex?: number;
  explanation?: string;
  prompt?: string;
  placeholder?: string;
  solution?: string;      // Correct answer for code challenges
  // DISABLED: task step-type
  // checklist?: string[];
  language?: string;      // Programming language for code challenges
  tests?: Array<{ description: string; testCode: string }>;  // Tests for code challenges
  
  // MULTI_SELECT properties
  correctIndices?: number[];
  allowPartial?: boolean;
  
  // FILL_IN_BLANK properties
  template?: string;
  blanks?: Array<{
    index: number;
    answer: string;
    alternatives?: string[];
    caseSensitive?: boolean;
  }>;
  
  // CODE_OUTPUT properties
  code?: string;
  correctOutput?: string;
  acceptableOutputs?: string[];
  
  // TYPING-TEST properties
  targetText?: string;
  testDuration?: number;
  showKeyboard?: boolean;
  
  // ASCII_CONNECT_DOTS properties
  decorativeTitle?: string;
  asciiArt?: string;
  nodes?: Array<{
    id: string;
    label: string;
    x: number;
    y: number;
    clickRadius?: number;
  }>;
  correctSequence?: string[];
  hint?: string;

  // RAPID_MATCH properties
  flashCard?: string;
  flashCardFormat?: 'text' | 'html' | 'code';
  flashCardLanguage?: string;
  durationMs?: number;
  keys?: string[];
  values?: string[];
  correctKey?: string;
  correctValue?: string;
};

interface LabSummary {
  id: number;
  title: string;
  slug: string;
  description?: string | null;
  courseId: number;
  course?: {
    id: number;
    title: string;
    slug: string;
  };
}

interface UserProgressRecord {
  id: number;
  userId: number;
  labId: number;
  courseId: number;
  completed: boolean;
  progress: number;
  currentStep: number;
  startedAt: string;
  completedAt?: string | null;
  lastAccessedAt: string;
  timeSpent: number;
  pointsEarned: number;
  lab: LabSummary;
}

interface CourseWithProgressResponse extends Course {
  completionPercentage: number;
  totalLabs: number;
  completedLabs: number;
  completed: boolean;
}

type LabProgressResponse = UserProgressRecord

interface UpdateLabProgressPayload {
  labId: number;
  completed?: boolean;
  progress?: number;
  currentStep?: number;
}

type UpdateLabProgressResponse = UserProgressRecord;

interface GamificationStatsResponse {
  user: {
    id: number;
    name?: string | null;
    email?: string | null;
    level: number;
    points: number;
    experience: number;
    streak: number;
    progressToNextLevel: number;
  };
  stats: {
    completedLabs: number;
    completedCourses: number;
    activeCourses: number;
    achievementsEarned: number;
  };
  achievements: Array<Achievement & { earnedAt: string }>;
  recentMilestones: Array<{ id: number; type: string; value: string; achievedAt: string; metadata?: unknown }>;
  recentPoints: Array<{ id: number; points: number; type: string; description?: string | null; createdAt: string }>;
}

interface Achievement {
  id: number;
  name: string;
  displayName: string;
  description: string;
  icon?: string | null;
  category: string;
  criteria: unknown;
  points: number;
  tier: string;
  isActive: boolean;
  earned?: boolean;
}

interface UserAchievement extends Achievement {
  earnedAt: string;
  progress?: number | null;
}

interface OnboardingIntakePayload {
  interests: string[];
  learningGoals: string;
  skillLevel?: string;
}

interface OnboardingIntakeResponse {
  id: number;
  intakeCompleted: boolean;
  interests: string[];
  learningGoals?: string | null;
  skillLevel?: string | null;
}

export interface UserSettings {
  videoDisabled: boolean;
  stickyMiniPlayerDisabled: boolean;
}

export interface Challenge {
  id: number;
  title: string;
  description: string;
  type: 'daily' | 'weekly' | 'special';
  category: string;
  criteria: object;
  points: number;
  startDate: string;
  endDate: string;
  maxCompletions: number;
  isActive: boolean;
  userProgress: {
    progress: number;
    completed: boolean;
    completions: number;
  } | null;
  timeRemaining: number;
}

export interface TtsVoice {
  id: string;
  name: string;
  gender?: string | null;
  previewUrl?: string | null;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api-content.technyc.io/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface CoursesResponse {
  accessLevel: 'free' | 'premium';
  canAccessPremium: boolean;
  accessibleCourses: Course[];
  lockedCourses: Course[];
  totalCourses: number;
  accessibleCount: number;
  lockedCount: number;
}

interface LabsResponse {
  accessLevel: 'free' | 'premium';
  canAccessPremium: boolean;
  accessibleLabs: Lab[];
  lockedLabs: Lab[];
  totalLabs: number;
  accessibleCount: number;
  lockedCount: number;
}

interface CourseDetailResponse {
  course: Course;
  hasAccess: boolean;
  isLocked: boolean;
  accessLevel: 'free' | 'premium';
  canAccessPremium: boolean;
  reason: string;
  labs: LabsResponse;
}

interface LabDetailResponse {
  lab: Lab;
  hasAccess: boolean;
  isLocked: boolean;
  accessLevel: 'free' | 'premium';
  canAccessPremium: boolean;
  reason: string;
 }
 
 interface StepAttemptResponse {
  stepAttempt: {
    id: number;
    stepIndex: number;
    attemptNumber: number;
    awardType: 'full' | 'partial' | 'loss';
    isCorrect: boolean;
    xpChange: number;
  };
  user: {
    experience: number;
    points: number;
    level: number;
  };
  message: string;
 }
 
 class ApiService {

  private async fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = getAuthTokenCookie();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    if (config.body && typeof config.body !== 'string') {
      config.body = JSON.stringify(config.body);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorBody = await response.json();
        errorMessage = errorBody?.error || errorBody?.message || errorMessage;
      } catch {
        // ignore parse error and use default message
      }
      const error = new Error(errorMessage) as Error & { status?: number };
      error.status = response.status;
      throw error;
    }

    const result: ApiResponse<T> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'API request failed');
    }

    return result.data as T;
  }

  // Courses API
  async getAllCourses(): Promise<CoursesResponse> {
    return this.fetchApi<CoursesResponse>('/courses');
  }

  async getCourseById(id: string): Promise<CourseDetailResponse> {
    return this.fetchApi<CourseDetailResponse>(`/courses/${id}`);
  }

  // Labs API
  async getAllLabs(): Promise<LabsResponse> {
    return this.fetchApi<LabsResponse>('/labs');
  }

  async getLabById(id: string): Promise<LabDetailResponse> {
    return this.fetchApi<LabDetailResponse>(`/labs/${id}`);
  }

  async getLabsByCourse(courseId: string): Promise<LabsResponse> {
    return this.fetchApi<LabsResponse>(`/courses/${courseId}/labs`);
  }

  async getUserProgress(): Promise<UserProgressRecord[]> {
    return this.fetchApi<UserProgressRecord[]>('/user/progress');
  }

  async getUserCoursesProgress(): Promise<CourseWithProgressResponse[]> {
    return this.fetchApi<CourseWithProgressResponse[]>('/user/courses');
  }

  async getLabProgress(labId: string): Promise<LabProgressResponse | null> {
    try {
      return await this.fetchApi<LabProgressResponse>(`/user/labs/${labId}`);
    } catch (error) {
      const status = (error as Error & { status?: number }).status;
      if (status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateLabProgress(payload: UpdateLabProgressPayload): Promise<UpdateLabProgressResponse> {
    return this.fetchApi<UpdateLabProgressResponse>('/user/progress', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async completeCourse(courseId: string): Promise<void> {
    return this.fetchApi<void>(`/user/courses/${courseId}/complete`, {
      method: 'POST',
    });
  }

  async saveOnboardingIntake(payload: OnboardingIntakePayload): Promise<OnboardingIntakeResponse> {
    return this.fetchApi<OnboardingIntakeResponse>('/user/onboarding/intake', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getUserSettings(): Promise<UserSettings> {
    return this.fetchApi<UserSettings>('/user/settings');
  }

  async updateUserSettings(payload: Partial<UserSettings>): Promise<UserSettings> {
    return this.fetchApi<UserSettings>('/user/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async getGamificationStats(): Promise<GamificationStatsResponse> {
    return this.fetchApi<GamificationStatsResponse>('/gamification/stats');
  }

  async getUserAchievements(): Promise<UserAchievement[]> {
    return this.fetchApi<UserAchievement[]>('/gamification/achievements');
  }

  async getAvailableAchievements(): Promise<Achievement[]> {
    return this.fetchApi<Achievement[]>('/gamification/achievements/available');
  }

  // Challenges API
  async getActiveChallenges(): Promise<Challenge[]> {
    return this.fetchApi<Challenge[]>('/challenges');
  }

  async updateChallengeProgress(challengeId: number, increment: number): Promise<object> {
    return this.fetchApi(`/challenges/${challengeId}/progress`, {
      method: 'POST',
      body: JSON.stringify({ increment }),
    });
  }

  // Analytics API
  async getUserAnalytics(days: number = 30): Promise<object> {
    return this.fetchApi(`/analytics/user?days=${days}`);
  }

  async getUserEngagementMetrics(days: number = 30): Promise<object> {
    return this.fetchApi(`/analytics/engagement?days=${days}`);
  }

  // Step Attempts API
  async recordStepAttempt(
    labId: number,
    stepIndex: number,
    outcome: 'full' | 'partial' | 'incorrect',
    partialCredit?: number
  ): Promise<StepAttemptResponse> {
    const payload: Record<string, unknown> = {
      labId,
      stepIndex,
      resultType: outcome,
    };

    if (typeof partialCredit === 'number') {
      payload.partialCredit = partialCredit;
    }

    return this.fetchApi<StepAttemptResponse>('/user/step-attempt', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
 
  async getStepAttempts(labId: number): Promise<object> {
    return this.fetchApi(`/user/step-attempt/${labId}`);
  }
 
  // Helper methods to maintain backward compatibility with existing frontend code
  async getCoursesAsRecord(): Promise<Record<string, Course>> {
    const response = await this.getAllCourses();
    const allCourses = [...response.accessibleCourses, ...response.lockedCourses];
    
    const coursesRecord: Record<string, Course> = {};
    allCourses.forEach(course => {
      coursesRecord[course.id] = course;
    });
    
    return coursesRecord;
  }


  async getLabsAsArray(): Promise<Lab[]> {
    const response = await this.getAllLabs();
    return [...response.accessibleLabs, ...response.lockedLabs];
  }
}

export const apiService = new ApiService();
export type {
  CoursesResponse,
  LabsResponse,
  CourseDetailResponse,
  LabDetailResponse,
  LabStep,
  UserProgressRecord,
  CourseWithProgressResponse,
  LabProgressResponse,
  UpdateLabProgressPayload,
  UpdateLabProgressResponse,
  GamificationStatsResponse,
  UserAchievement,
  Achievement,
  OnboardingIntakePayload,
  OnboardingIntakeResponse,
  // Challenge, // Already exported as interface above
};
